import React, { useMemo, useCallback } from 'react';

import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import cn from 'classnames';

import Logger from '../../services/Logger';
import ReactSelect from '../../UIComponents/ReactSelect';
import {
  changeOrder,
} from '../../store/products/actions';
import {
  buildSelectStyles,
  resolvePlaceholderColor,
  getDefaultSelectStyleTokens,
} from './helpers';
import { showDialog } from '../dialog';
import l18n from '../../shared/strings';
import * as utils from '../../shared/utils';
import Tooltip from '../Tooltip';

const ImageOrder = (props) => {
  const dispatch = useDispatch();
  const {
    order,
    productId,
    productAssetsLength,
    assetId,
    disabled,
    isImage,
    viewContext,
    tooltip = l18n.mainImageTooltip,
  } = props;

  const orderOptions = useMemo(() => Array.from({ length: productAssetsLength }, (_, index) => ({
    label: index === 0 ? '1 (Main image)' : `${index + 1}`,
    value: index,
    isDisabled: !isImage && index === 0,
  })), [isImage, productAssetsLength]);

  const selectedOption = useMemo(() => {
    const option = orderOptions.find((o) => o.value === order?.index) || null;
    if (option && option.value === 0) return { ...option, label: '1' };
    return option;
  }, [orderOptions, order]);

  const placeholderColor = useMemo(() => resolvePlaceholderColor('#999'), []);
  const styleTokens = useMemo(() => getDefaultSelectStyleTokens(placeholderColor), [placeholderColor]);
  const styles = useMemo(() => buildSelectStyles(styleTokens), [styleTokens]);

  const confirm = useCallback((newOrder) => {
    Logger.log('User', 'ProductsReorderMediaModalChangesConfirmed', {
      productId,
      positionN: newOrder.value,
      assetId,
    });
    dispatch(changeOrder({
      assetId,
      order: newOrder.value,
      productId,
      viewContext,
    }));
  }, [dispatch, assetId, productId, viewContext]);

  const handleChange = useCallback((newOrder) => {
    const notShowDialogReorderMedia = utils.LocalStorage.get('picsio.notShowDialogReorderMedia') || false;
    if (notShowDialogReorderMedia) {
      confirm(newOrder);
      return;
    }
    Logger.log('User', 'ProductsReorderMediaPositionSelected', {
      assetId,
      positionN: newOrder.value,
      productId,
    });

    showDialog({
      title: l18n.DIALOGS.CHANGE_IMAGE_ORDER.TITLE,
      text: l18n.DIALOGS.CHANGE_IMAGE_ORDER.TEXT(),
      textBtnOk: l18n.DIALOGS.CHANGE_IMAGE_ORDER.textBtnOk,
      textBtnCancel: l18n.DIALOGS.CHANGE_IMAGE_ORDER.textBtnCancel,
      onOk: ({ checkbox }) => {
        if (checkbox) {
          utils.LocalStorage.set('picsio.notShowDialogReorderMedia', checkbox);
        }
        confirm(newOrder);
      },
      onCancel: () => {
        Logger.log('User', 'ProductsReorderMediaModalClosed');
      },
      checkbox: {
        value: false,
        label: l18n.DIALOGS.CHANGE_IMAGE_ORDER.checkboxLabel,
      },
    });
  }, [assetId, confirm, productId]);

  const handleClick = useCallback(() => {
    Logger.log('User', 'ProductsReorderMediaDropdownOpened', {
      assetId,
      productId,
    });
  }, [assetId, productId]);

  const customFilterOption = useCallback((option, inputValue) => option.label.toLowerCase().startsWith(inputValue.toLowerCase()), []);

  return (
    <Tooltip
      placement="top"
      content={tooltip}
    >
      <div className={cn('imageOrder', { disabled })}>
        <ReactSelect
          options={orderOptions}
          value={selectedOption}
          menuPortalTarget={document.body}
          styles={styles}
          isDisabled={disabled}
          filterOption={customFilterOption}
          onClick={handleClick}
          onChange={handleChange}
        />
      </div>
    </Tooltip>
  );
};

ImageOrder.propTypes = {
  order: PropTypes.shape({
    productId: PropTypes.string,
    index: PropTypes.number,
  }),
  productId: PropTypes.string,
  productAssetsLength: PropTypes.number.isRequired,
  assetId: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  isImage: PropTypes.bool,
  tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.func]),
  viewContext: PropTypes.shape({
    masterProductId: PropTypes.string,
    listingProductId: PropTypes.string,
    channelId: PropTypes.string,
    layer: PropTypes.string,
  }),
};

ImageOrder.defaultProps = {
  order: null,
  productId: null,
  disabled: false,
  isImage: true,
};

export default ImageOrder;
